import React from 'react';
import PropTypes from 'prop-types';
import { bemGenerator } from '@prisjakt/ui-base';
import T from '@prisjakt/ui-translate';
import Tooltip from '@prisjakt/ui-tooltip';
import { connect } from 'react-redux';
import EyeIcon from 'mdi-react/EyeIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';

import { AppContext } from '@prisjakt/ui-app-context';
import filterActions from '../../../../actions/filter';
import isNumeric from '../../../../../../globals/helpers/is-numeric';
import Filter from '../filter';
import ErrorBoundary from '../../../../../../components/error-boundary';

import * as S from './styles';
import './index.scss';

const styles = bemGenerator('filter-panel', [
  'inner',
  'header',
  'title',
  'filter',
  'title-wrapper',
  'unit',
  'compare-icon',
  'clear-icon',
  'show-all-icon',
]);

class FilterPanel extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showAllValues: {},
    };
  }

  handleToggleValues(id) {
    this.setState(prevState => ({
      showAllValues: {
        ...prevState.showAllValues,
        [id]: !prevState.showAllValues[id],
      },
    }));
  }

  static getListItemLimit({ type }) {
    switch (type) {
      case 'list-tree-and':
      case 'list-tree-or': {
        return 4;
      }
      case 'list-or':
      case 'list-and':
      case 'list-or-reduced':
      case 'list-and-reduced': {
        return 5;
      }
      default: {
        return undefined;
      }
    }
  }

  render() {
    const {
      count,
      filterQuery,
      filterChangeAction,
      filteredValues,
      isBrandFilteredCategory,
      query,
      hideName,
      canCompare,
      comparing,
      compareProperty,
      layout,
      categoryId,
    } = this.props;

    const { id, name, children, unit } = this.props.displayFilter;
    const { t } = this.context;
    const values = filteredValues[id] && filteredValues[id].values;

    // Do not render filters with no values
    if (values && values.length === 0) {
      return null;
    }

    // Never render the Brand-filter if we're in a Brand Filtered Category
    if (isBrandFilteredCategory && id === 'brands') {
      return null;
    }

    const limitValues = FilterPanel.getListItemLimit(this.props.displayFilter);

    return (
      <ErrorBoundary onError={() => null}>
        <div
          className={styles.block.withModifier({
            'hide-line': hideName || !!children,
            [layout]: true,
          })}
          role="group"
          aria-labelledby={`filter-panel-title-${id}`}
          data-test="FilterPanel"
          data-test-title={id}
        >
          <div
            className={styles.inner.withModifier({
              [layout]: true,
            })}
          >
            {!hideName && (
              <div
                className={styles.header.withModifier({
                  [layout]: true,
                })}
              >
                <div
                  id={`filter-panel-title-${id}`}
                  className={styles['title-wrapper']}
                >
                  <h6
                    className={styles.title.withModifier({
                      counter: count,
                    })}
                  >
                    {name}
                  </h6>
                  {unit && <span className={styles.unit}>({unit})</span>}
                </div>
                <button
                  type="button"
                  aria-label={t('filter', 'button.clear-filter')}
                  className={styles['clear-icon'].withModifier({
                    hidden: query[id] === undefined,
                  })}
                  onClick={() => {
                    const newQuery = { ...query };
                    delete newQuery[id];
                    filterQuery(newQuery);
                    filterChangeAction(categoryId, newQuery);
                  }}
                >
                  <DeleteIcon />
                  <S.ClearFilterTooltip position="left">
                    <T domain="filter">button.clear-filter</T>
                  </S.ClearFilterTooltip>
                </button>
                {isNumeric(id) && canCompare && (
                  <button
                    type="button"
                    aria-label={t('filter', 'properties.toggle-column')}
                    aria-pressed={String(comparing.includes(id))}
                    className={styles['compare-icon'].withModifier({
                      selected: comparing.includes(id),
                      enabled: true,
                    })}
                    onClick={() => {
                      compareProperty(id);
                    }}
                  >
                    <EyeIcon />
                    <S.ToggleColumnTooltip position="left">
                      <T domain="filter">properties.toggle-column</T>
                    </S.ToggleColumnTooltip>
                  </button>
                )}
                {layout === 'horizontal' && (
                  <button
                    type="button"
                    aria-label={
                      this.state.showAllValues[id]
                        ? t('filter', 'properties.quantity-show-less')
                        : t('filter', 'properties.quantity-show-more')
                    }
                    className={styles['show-all-icon'].withModifier({
                      hidden:
                        !values || !limitValues || limitValues >= values.length,
                    })}
                    onClick={() => {
                      this.handleToggleValues(id);
                    }}
                    data-test="MoreFiltersToggleButton"
                  >
                    {this.state.showAllValues[id] ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                    <Tooltip position="top">
                      {this.state.showAllValues[id] ? (
                        <T domain="filter">properties.quantity-show-less</T>
                      ) : (
                        <T domain="filter">properties.quantity-show-more</T>
                      )}
                    </Tooltip>
                  </button>
                )}
              </div>
            )}
            <div className={styles.filter}>
              <Filter
                {...this.props}
                limitValues={limitValues}
                showAllValues={Boolean(this.state.showAllValues[id])}
                onToggleAllValues={panelId => {
                  this.handleToggleValues(panelId);
                }}
                filterChangeAction={filterChangeAction}
                filteredValues={filteredValues}
              />
            </div>
          </div>
          {children &&
            children.map(filter => {
              if (
                filter.type === 'boolean' ||
                filter.type === 'string' ||
                filter.children ||
                (filteredValues[filter.id] && filteredValues[filter.id].values)
              ) {
                return (
                  <FilterPanel
                    categoryId={categoryId}
                    key={filter.id}
                    layout={layout}
                    displayFilter={filter}
                    filterQuery={filterQuery}
                    filterChangeAction={filterChangeAction}
                    filteredValues={filteredValues}
                    comparing={comparing}
                    compareProperty={compareProperty}
                    query={query}
                  />
                );
              }
              return null;
            })}
        </div>
      </ErrorBoundary>
    );
  }
}

FilterPanel.contextType = AppContext;

const { arrayOf, oneOf, number, object, func, bool } = PropTypes;

FilterPanel.propTypes = {
  count: number,
  query: object, // eslint-disable-line react/forbid-prop-types
  isBrandFilteredCategory: bool,
  displayFilter: object.isRequired, // eslint-disable-line react/forbid-prop-types
  canCompare: bool,
  comparing: arrayOf(number),
  compareProperty: func,
  filterChangeAction: func,
  filterQuery: func,
  filteredValues: object.isRequired, // eslint-disable-line react/forbid-prop-types
  hideName: bool,
  layout: oneOf(['horizontal', 'vertical']),
};

FilterPanel.defaultProps = {
  count: 0,
  query: {},
  isBrandFilteredCategory: false,
  filterChangeAction: () => {},
  hideName: false,
  canCompare: true,
  comparing: [],
  layout: 'vertical',
  filterQuery: undefined,
  compareProperty: () => {},
};

export default connect(
  state => state.areas.category.filter,
  filterActions
)(FilterPanel);
